import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Navbar } from "@/components/venture/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { API_BASE } from "@/lib/api";
import { startRecommendationFlow } from "@/lib/cta";
import { useAuth } from "@/context/AuthContext";
import { optimizeImg, srcSetFor } from "@/lib/imgUrl";
import SeoFranchiseCarousel from "@/components/venture/SeoFranchiseCarousel";
import { trackCategoryExplored } from "@/utils/tracker";
import {
  ChevronRight, Sparkles, ArrowRight, MapPin, Wallet, TrendingUp,
  ShieldCheck, Target, Home as HomeIcon,
} from "lucide-react";

const seoAPI = axios.create({ baseURL: API_BASE });

const INV_BAND_KEYS = [
  { slug: "under-5-lakh", key: "seo_landing.band_under_5" },
  { slug: "under-10-lakh", key: "seo_landing.band_under_10" },
  { slug: "under-25-lakh", key: "seo_landing.band_under_25" },
  { slug: "under-50-lakh", key: "seo_landing.band_under_50" },
  { slug: "above-1-crore", key: "seo_landing.band_above_1" },
];

function useLanding(level, params) {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true); setNotFound(false); setData(null);
    let url = "";
    if (level === "industry") url = `/seo/landing/industry/${params.industry}`;
    else if (level === "venture-type") url = `/seo/landing/venture-type/${params.ventureType}`;
    else if (level === "budget") url = `/seo/landing/budget/${params.band}`;
    seoAPI.get(url)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [level, params.industry, params.ventureType, params.band]);
  return { data, notFound, loading };
}

function fmtCap(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${Math.round(n / 100000)}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function Breadcrumbs({ items }) {
  if (!items?.length) return null;
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5" data-testid="landing-breadcrumbs">
      {items.map((it, i) => {
        const last = i === items.length - 1;
        const path = it.href ? new URL(it.href, "http://x").pathname : "";
        return (
          <span key={i} className="inline-flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 text-slate-300" />}
            {last ? (
              <span className="text-slate-700 font-medium">{it.name}</span>
            ) : (
              <Link to={path || "/"} className="hover:text-emerald-700">
                {i === 0 ? <HomeIcon className="h-3 w-3 inline mr-1 -mt-0.5" /> : null}
                {it.name}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 min-w-0" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1 font-heading text-lg font-bold text-slate-900 break-words">{value}</div>
    </div>
  );
}

function VentureCard({ v }) {
  const { t } = useTranslation();
  return (
    <Card className="card-lift rounded-2xl overflow-hidden border-slate-200" data-testid={`landing-venture-${v.slug}`}>
      <div className="h-32 w-full overflow-hidden bg-gradient-to-br from-emerald-100 to-slate-100">
        <img
          src={optimizeImg(v.image_url, 480)}
          srcSet={srcSetFor(v.image_url, [320, 480, 640])}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          alt={v.name}
          width="600"
          height="200"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Badge className="rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-100 capitalize">
            {t("seo_landing.risk_suffix", { risk: v.risk_level || "medium" })}
          </Badge>
          <span className="text-[10px] uppercase text-slate-500 truncate">{v.industry}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-bold text-slate-900 break-words leading-snug">{v.name}</h3>
        <p className="mt-1 text-xs text-slate-500 line-clamp-2">{v.tagline}</p>
        <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" /> {fmtCap(v.min_capital)}–{fmtCap(v.max_capital)}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {(v.ideal_tiers || []).join(", ")}</span>
        </div>
        <Button asChild variant="ghost" className="mt-4 rounded-full px-0 text-emerald-700 hover:text-emerald-800">
          <Link to={`/venture/${v.slug}`}>{t("seo_landing.read_playbook")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function CtaBanner({ primary, secondary, small }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  return (
    <Card className={"rounded-3xl border-none bg-navy text-white overflow-hidden " + (small ? "" : "shadow-[0_20px_60px_rgba(10,31,68,0.25)]")}>
      <CardContent className="p-6 sm:p-10 grid gap-6 md:grid-cols-3 items-center">
        <div className="md:col-span-2 min-w-0">
          <Badge className="rounded-full bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/20">
            <Sparkles className="h-3 w-3 mr-1" /> {t("seo_landing.cta_free_assessment")}
          </Badge>
          <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold leading-snug break-words">
            {t("seo_landing.cta_banner_title")}
          </h2>
          <p className="mt-2 text-white/80 text-sm sm:text-base">
            {t("seo_landing.cta_banner_sub")}
          </p>
        </div>
        <div className="text-center md:text-right space-y-2">
          <Button
            data-testid="landing-primary-cta"
            onClick={() => startRecommendationFlow({ user, nav })}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-navy font-heading font-bold h-12 px-6 w-full md:w-auto"
          >
            {t("seo_landing.cta_get_recs", "Find My Best-Fit Business")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div>
            <Button
              data-testid="landing-secondary-cta"
              variant="ghost"
              onClick={() => nav(secondary?.href || "/business-ideas")}
              className="rounded-full text-white/80 hover:text-white hover:bg-white/10"
            >
              {t("seo_landing.cta_browse", "Browse Business Ideas")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FaqBlock({ faqs }) {
  const { t } = useTranslation();
  if (!faqs?.length) return null;
  return (
    <section className="mt-14" data-testid="landing-faq">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.faq_title")}</h2>
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white">
        <Accordion type="single" collapsible>
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-100 last:border-b-0 px-5">
              <AccordionTrigger className="hover:no-underline py-4 font-heading font-semibold text-slate-900 text-left break-words">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-4 text-slate-700 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function LandingBody({ data, level }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const jsonLd = data.json_ld || [];
  const meta = data.meta || {};
  const hero = data.hero || {};
  const industry = data.industry;
  const ventureType = data.venture_type;

  // R110.99 — Anchor slug for the SEO franchise carousel + sticky bar.
  // Priority: venture_type > industry > top popular venture.
  const anchorSlug =
    (level === "venture_type" && ventureType?.slug) ||
    (level === "industry" && industry?.slug) ||
    data.popular_ventures?.[0]?.slug ||
    data.all_ventures?.[0]?.slug ||
    null;
  const anchorName =
    (level === "venture_type" && ventureType?.name) ||
    (level === "industry" && industry?.name) ||
    data.band?.label ||
    null;
  const pageType = level === "investment_band" ? "hybrid" : "business_idea";

  // Passive tracker — record this category browse in idle.
  useEffect(() => {
    if (!anchorSlug) return;
    const t2 = setTimeout(() => {
      trackCategoryExplored(anchorSlug, {
        category_name: anchorName,
        page_type: pageType,
        city_name: null,
      });
    }, 400);
    return () => clearTimeout(t2);
  }, [anchorSlug, anchorName, pageType]);

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="robots" content={meta.robots || "index,follow"} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:image" content={meta.og_image} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.og_image} />
        {jsonLd.map((obj, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        ))}
      </Helmet>

      <section className="grid-noise text-white relative overflow-hidden">
        {(industry?.banner_image || data.band?.slug) && (
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url(${industry?.banner_image || data.top_venture?.hero_image || ""})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "grayscale(0.2)",
            }}
            aria-hidden="true"
          />
        )}
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-12 lg:py-16">
          <Breadcrumbs items={data.breadcrumbs} />
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" /> {hero.eyebrow}
          </div>
          <h1 data-testid="landing-h1" className="font-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight break-words">
            {hero.h1}
          </h1>
          <p className="mt-5 text-slate-300 text-lg max-w-3xl">{hero.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="landing-hero-primary-cta"
              onClick={() => startRecommendationFlow({ user, nav })}
              className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-6"
            >
              {t("seo_landing.cta_get_recs", "Find My Best-Fit Business")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              data-testid="landing-hero-secondary-cta"
              variant="outline"
              onClick={() => nav(data.secondary_cta?.href || "/business-ideas")}
              className="rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white px-6 py-6"
            >
              {t("seo_landing.cta_browse", "Browse Business Ideas")}
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        {/* Stats row */}
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
          <StatTile icon={Target} label={level === "investment_band" ? t("seo_landing.stat_ventures") : t("seo_landing.stat_ventures_listed")} value={data.stats?.venture_count} />
          {level === "industry" && (
            <StatTile icon={ShieldCheck} label={t("seo_landing.stat_venture_types")} value={data.stats?.venture_type_count} />
          )}
          {level === "venture_type" && (
            <StatTile icon={ShieldCheck} label={t("seo_landing.stat_risk_profile")} value={data.stats?.risk_profile} />
          )}
          {level === "investment_band" && (
            <StatTile icon={ShieldCheck} label={t("seo_landing.stat_industries")} value={data.stats?.industry_count} />
          )}
          <StatTile icon={Wallet} label={t("seo_landing.stat_investment_range")} value={data.stats?.invest_display} />
          <StatTile icon={TrendingUp} label={level === "venture_type" ? t("seo_landing.stat_growth_potential") : t("seo_landing.stat_avg_margin")} value={data.stats?.growth_potential || (data.stats?.avg_margin_pct ? t("seo_landing.stat_pa", { pct: data.stats.avg_margin_pct }) : t("seo_landing.stat_high"))} />
        </div>

        {/* Overview */}
        <section className="mt-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
            {level === "industry" && t("seo_landing.about_industry", { name: industry?.name })}
            {level === "venture_type" && t("seo_landing.about_venture_type", { name: ventureType?.name })}
            {level === "investment_band" && t("seo_landing.businesses_band", { band: data.band?.label?.toLowerCase() })}
          </h2>
          <p className="mt-3 text-slate-700 max-w-3xl leading-relaxed">
            {level === "industry" && (industry?.description || hero.sub)}
            {level === "venture_type" && (ventureType?.description || hero.sub)}
            {level === "investment_band" && hero.sub}
          </p>

          {level === "venture_type" && data.suitable_for?.length > 0 && (
            <div className="mt-6" data-testid="landing-suitable-for">
              <h3 className="font-heading text-lg font-bold text-slate-900">{t("seo_landing.suitable_for_title")}</h3>
              <ul className="mt-3 space-y-2">
                {data.suitable_for.map((s, i) => (
                  <li key={i} className="flex gap-2 text-slate-700 leading-relaxed">
                    <ShieldCheck className="h-4 w-4 text-emerald-600 mt-1 shrink-0" />
                    <span className="break-words">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Popular / All ventures */}
        {level === "industry" && data.popular_ventures?.length > 0 && (
          <section className="mt-12" data-testid="landing-popular-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.popular_ventures_in", { name: industry?.name })}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.popular_ventures.slice(0, 6).map((v) => <VentureCard key={v.slug} v={v} />)}
            </div>
          </section>
        )}

        {/* Types grid (industry landing only) */}
        {level === "industry" && data.types?.length > 0 && (
          <section className="mt-12" data-testid="landing-types-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.explore_by_type")}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.types.map((tp) => {
                const count = (data.ventures_by_type?.[tp.slug] || []).length;
                if (count === 0) return null;
                return (
                  <Link
                    key={tp.slug}
                    to={`/business-ideas/${industry.slug}/${tp.slug}`}
                    data-testid={`landing-type-${tp.slug}`}
                    className="rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 p-4 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading text-base font-bold text-slate-900 break-words leading-snug">{tp.name}</div>
                        <div className="mt-1 text-xs text-slate-500 line-clamp-2">{tp.description || t("seo_landing.sub_ventures_inside", { count, name: tp.name })}</div>
                      </div>
                      <Badge variant="outline" className="rounded-full text-xs whitespace-nowrap shrink-0">{count}</Badge>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* R110.99 — SEO franchise carousel (non-intrusive injection).
            Placed between the primary content H2s and the mid-page CTA
            so it complements the organic SEO copy without pushing it
            below the fold. */}
        {anchorSlug ? (
          <SeoFranchiseCarousel
            ideaSlug={anchorSlug}
            headline={anchorName ? `Verified Franchise Brands · ${anchorName}` : null}
          />
        ) : null}

        {/* Mid-page CTA */}
        <section className="mt-12" data-testid="landing-mid-cta">
          <CtaBanner primary={data.primary_cta} secondary={data.secondary_cta} />
        </section>

        {/* All ventures list */}
        {data.all_ventures?.length > 0 && (
          <section className="mt-14" data-testid="landing-all-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {level === "venture_type" ? t("seo_landing.all_sub_ventures") : t("seo_landing.all_ventures")}
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.all_ventures.map((v) => <VentureCard key={v.slug} v={v} />)}
            </div>
          </section>
        )}

        {/* Related types (venture-type page) */}
        {level === "venture_type" && data.related_types?.length > 0 && (
          <section className="mt-14" data-testid="landing-related-types">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {t("seo_landing.other_types", { name: industry?.name.toLowerCase() })}
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.related_types.map((tp) => (
                <Link
                  key={tp.slug}
                  to={`/business-ideas/${industry.slug}/${tp.slug}`}
                  data-testid={`landing-related-type-${tp.slug}`}
                  className="rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 p-4 transition-colors"
                >
                  <div className="font-heading text-base font-bold text-slate-900 break-words leading-snug">{tp.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{tp.description || t("seo_landing.explore_type", { name: tp.name })}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Related ventures (venture-type page) */}
        {level === "venture_type" && data.related_ventures?.length > 0 && (
          <section className="mt-14" data-testid="landing-related-ventures">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.related_ventures")}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.related_ventures.map((v) => <VentureCard key={v.slug} v={v} />)}
            </div>
          </section>
        )}

        {/* Industry list (budget-band page) */}
        {level === "investment_band" && data.industries?.length > 0 && (
          <section className="mt-14" data-testid="landing-industries-section">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.available_industries")}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.industries.map((ind) => (
                <Link
                  key={ind.slug}
                  to={`/business-ideas/${ind.slug}`}
                  data-testid={`landing-industry-${ind.slug}`}
                  className="rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 p-4 transition-colors"
                >
                  <div className="font-heading text-base font-bold text-slate-900 break-words leading-snug">{ind.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{ind.description}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Budget-band cross-links */}
        <section className="mt-14" data-testid="landing-budget-crosslinks">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("seo_landing.browse_by_investment")}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {INV_BAND_KEYS.map((b) => (
              <Link
                key={b.slug}
                to={`/business-ideas/budget/${b.slug}`}
                data-testid={`inv-band-link-${b.slug}`}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:border-emerald-300"
              >
                {t("seo_landing.businesses_lower", { band: t(b.key).toLowerCase() })}
              </Link>
            ))}
          </div>
        </section>

        <FaqBlock faqs={data.faqs} />

        <div className="mt-14">
          <CtaBanner primary={data.primary_cta} secondary={data.secondary_cta} />
        </div>
      </div>

    </>
  );
}

export function IndustryLanding() {
  // R110.42 — Route path is /business-ideas/:seg1 (dispatched via
  // BusinessIdeasSlugRouter). Read `seg1` (never `industry`).
  const { seg1: industry } = useParams();
  const { data, notFound, loading } = useLanding("industry", { industry });
  return <LandingShell data={data} loading={loading} notFound={notFound} level="industry" resolvedLevel={data?.level} />;
}

export function VentureTypeLanding() {
  // R110.42 — Route path is /business-ideas/:seg1/:seg2.
  const { seg1: industry, seg2: ventureType } = useParams();
  const { data, notFound, loading } = useLanding("venture-type", { ventureType });
  const nav = useNavigate();
  // If URL industry doesn't match backend industry, redirect to canonical
  useEffect(() => {
    if (data && data.industry && data.industry.slug !== industry) {
      nav(`/business-ideas/${data.industry.slug}/${ventureType}`, { replace: true });
    }
  }, [data, industry, ventureType, nav]);
  return <LandingShell data={data} loading={loading} notFound={notFound} level="venture_type" resolvedLevel={data?.level} />;
}

export function InvestmentBandLanding() {
  const { band } = useParams();
  const { data, notFound, loading } = useLanding("budget", { band });
  return <LandingShell data={data} loading={loading} notFound={notFound} level="investment_band" resolvedLevel={data?.level} />;
}

function LandingShell({ data, loading, notFound, level, resolvedLevel }) {
  const nav = useNavigate();
  const { t } = useTranslation();
  return (
    <div>
      <Navbar />
      {loading && (
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-16 space-y-6">
          <Skeleton className="h-8 w-72 rounded-full" />
          <Skeleton className="h-20 w-full max-w-2xl rounded-2xl" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
          </div>
        </div>
      )}
      {notFound && (
        <>
          <Helmet>
            <title>Page not found | VentureKhoj™</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className="mx-auto max-w-3xl px-5 sm:px-8 py-24 text-center">
            <h1 className="font-heading text-3xl font-bold text-slate-900">{t("seo_landing.notfound_title")}</h1>
            <p className="mt-3 text-slate-600">{t("seo_landing.notfound_sub")}</p>
            <Button data-testid="landing-notfound-cta" onClick={() => nav("/business-ideas")} className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {t("seo_landing.notfound_cta")}
            </Button>
          </div>
        </>
      )}
      {!loading && !notFound && data && <LandingBody data={data} level={resolvedLevel || level} />}
    </div>
  );
}

export default IndustryLanding;
